/**
 * Microsoft Clarity Analytics Service for Worker UI
 * Provides session recordings and user behavior tracking for worker sessions
 */

declare global {
  interface Window {
    clarity?: (action: string, ...args: any[]) => void;
  }
}

interface ClarityConfig {
  projectId: string;
  debug?: boolean;
}

interface WorkerSession {
  userId: string;
  workerId: string;
  sessionId: string;
  customTags?: Record<string, string>;
}

export class WorkerClarityService {
  private static instance: WorkerClarityService;
  private projectId: string;
  private initialized: boolean = false;
  private debug: boolean = false;
  private currentUserId: string | null = null;
  private workerId: string | null = null;

  private constructor() {
    // You'll need to get your Clarity project ID from https://clarity.microsoft.com
    // Consider using the same or different project ID for worker vs landing VM
    this.projectId = import.meta.env.VITE_CLARITY_PROJECT_ID || 'tb848hyy02';
    this.debug = true; // Enable debug for now
    console.log('Worker Clarity: Constructor initialized with project ID:', this.projectId);
  }

  static getInstance(): WorkerClarityService {
    if (!WorkerClarityService.instance) {
      WorkerClarityService.instance = new WorkerClarityService();
    }
    return WorkerClarityService.instance;
  }

  /**
   * Initialize Microsoft Clarity for Worker UI
   */
  async initialize(config?: ClarityConfig): Promise<void> {
    if (this.initialized) return;

    const projectId = config?.projectId || this.projectId;

    if (!projectId) {
      console.warn('Worker Clarity: No project ID provided');
      return;
    }

    this.projectId = projectId;
    if (config?.debug !== undefined) {
      this.debug = config.debug;
    }

    // Load Clarity script. The loader stub exists synchronously, so the consent
    // signal queues ahead of the tag: this only runs once the consent gate allows it.
    const loaded = this.loadClarityScript();
    window.clarity?.('consent');
    await loaded;

    this.initialized = true;

    // Set worker-specific tags
    this.setTag('app_type', 'worker_ui');
    this.setTag('worker_hostname', window.location.hostname);

    if (this.debug) {
      console.log('Worker Clarity: Initialized with project ID', projectId);
    }
  }

  /**
   * Load Clarity tracking script
   */
  private async loadClarityScript(): Promise<void> {
    return new Promise((resolve) => {
      // Clarity initialization script
      (function(c: any, l: any, a: any, r: any, i: any) {
        let t: any, y: any;
        c[a] = c[a] || function() { (c[a].q = c[a].q || []).push(arguments) };
        t = l.createElement(r);
        t.async = 1;
        t.src = "https://www.clarity.ms/tag/" + i;
        y = l.getElementsByTagName(r)[0];
        y.parentNode.insertBefore(t, y);

        // Resolve when script is loaded
        t.onload = () => resolve();
      })(window, document, "clarity", "script", this.projectId);
    });
  }

  /**
   * Identify worker session
   */
  identifyWorkerSession(session: WorkerSession): void {
    if (!this.initialized || !window.clarity) return;

    const { userId, workerId, sessionId, customTags } = session;

    this.currentUserId = userId;
    this.workerId = workerId;

    // Identify user with worker context
    window.clarity('identify', userId, sessionId, {
      worker_id: workerId,
      session_type: 'worker',
      ...customTags
    });

    // Set additional worker tags
    this.setTags({
      user_id: userId,
      worker_id: workerId,
      session_id: sessionId,
      session_start: new Date().toISOString()
    });

    if (this.debug) {
      console.log('Worker Clarity: Session identified', session);
    }
  }

  /**
   * Set custom tag
   */
  setTag(key: string, value: string): void {
    if (!this.initialized || !window.clarity) return;

    window.clarity('set', key, value);

    if (this.debug) {
      console.log('Worker Clarity: Tag set', { key, value });
    }
  }

  /**
   * Set multiple tags
   */
  setTags(tags: Record<string, string>): void {
    Object.entries(tags).forEach(([key, value]) => {
      this.setTag(key, value);
    });
  }

  /**
   * Track worker-specific events
   */
  trackWorkerEvents = {
    sessionStarted: (userId: string, workerId: string) => {
      this.identifyWorkerSession({
        userId,
        workerId,
        sessionId: `worker-${Date.now()}`
      });
      this.setTag('event', 'worker_session_started');
    },

    terminalOpened: () => {
      this.setTag('event', 'terminal_opened');
      this.setTag('feature_terminal', 'used');
    },

    fileEdited: (fileName: string) => {
      this.setTags({
        event: 'file_edited',
        file_type: fileName.split('.').pop() || 'unknown'
      });
    },

    commandExecuted: (commandType: string) => {
      this.setTag('event', `command_${commandType}`);
    },

    aiAssistantUsed: (feature: string) => {
      this.setTags({
        event: 'ai_assistant_used',
        ai_feature: feature
      });
    },

    errorOccurred: (errorType: string) => {
      this.setTags({
        event: 'error_occurred',
        error_type: errorType,
        error_time: new Date().toISOString()
      });
    },

    sessionEnded: (duration: number) => {
      this.setTags({
        event: 'worker_session_ended',
        session_duration: Math.round(duration / 1000).toString() // Convert to seconds
      });
    }
  };

  /**
   * Track IDE features usage
   */
  trackIDEFeatures = {
    editorOpened: (language: string) => {
      this.setTags({
        event: 'editor_opened',
        editor_language: language
      });
    },

    debuggerStarted: () => {
      this.setTag('event', 'debugger_started');
      this.setTag('feature_debugger', 'used');
    },

    extensionInstalled: (extensionName: string) => {
      this.setTags({
        event: 'extension_installed',
        extension_name: extensionName
      });
    },

    themeChanged: (themeName: string) => {
      this.setTags({
        event: 'theme_changed',
        theme_name: themeName
      });
    },

    projectCreated: (projectType: string) => {
      this.setTags({
        event: 'project_created',
        project_type: projectType
      });
    },

    gitOperation: (operation: string) => {
      this.setTags({
        event: 'git_operation',
        git_command: operation
      });
    }
  };

  /**
   * Track performance metrics
   */
  trackPerformance(metric: string, value: number): void {
    this.setTags({
      [`perf_${metric}`]: value.toString(),
      perf_timestamp: new Date().toISOString()
    });
  }

  /**
   * Track page/view navigation within worker UI
   */
  trackNavigation(viewName: string): void {
    this.setTags({
      current_view: viewName,
      navigation_time: new Date().toISOString()
    });
  }

  /**
   * Mask sensitive content in recordings
   */
  maskSensitiveContent(selector: string): void {
    if (!this.initialized || !window.clarity) return;

    window.clarity('mask', selector);
  }

  /**
   * Track custom events
   */
  trackEvent(eventName: string, properties?: Record<string, any>): void {
    if (!this.initialized) return;

    this.setTag('event', eventName);

    if (properties) {
      const tags: Record<string, string> = {};
      Object.entries(properties).forEach(([key, value]) => {
        tags[key] = String(value).substring(0, 255);
      });
      this.setTags(tags);
    }

    if (this.debug) {
      console.log('Worker Clarity: Event tracked', { eventName, properties });
    }
  }

  /**
   * Upgrade session for important events
   */
  upgradeSession(reason: string): void {
    if (!this.initialized || !window.clarity) return;

    window.clarity('upgrade', reason);

    if (this.debug) {
      console.log('Worker Clarity: Session upgraded', { reason });
    }
  }

  /**
   * Check if initialized
   */
  isInitialized(): boolean {
    return this.initialized && typeof window.clarity === 'function';
  }

  /**
   * Get current session info
   */
  getSessionInfo(): { userId: string | null; workerId: string | null } {
    return {
      userId: this.currentUserId,
      workerId: this.workerId
    };
  }
}

// Export singleton instance. Not auto-initialized: main.tsx initializes it through the
// consent gate (src/analytics/consentGate.ts), only when analytics is allowed.
export const workerClarity = WorkerClarityService.getInstance();